import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import * as gql from '@/graphql'
import { numFormat } from '@/utils/num_format'
import dayjs from 'dayjs'

interface CardItem {
  label: string
  value: string
  showTip?: boolean
  tipContent?: string
}

export interface TableColumn {
  prop: string
  label: string
  minWidth?: string
  fixed?: string
  align?: string
  showTip?: boolean
  tipContent?: string
  type?: 'action' | 'twoLines' | 'tagWithText'
  propList?: { prop: string, style?: 'tag' }[]
  separator?: string
}

interface UseAccountDetailOptions {
  clientId: Ref<string>
  activeTopTab: Ref<string>
  activeSubTab: Ref<string>
}

interface UseAccountDetailReturn {
  loading: Ref<boolean>
  currentCards: ComputedRef<CardItem[]>
  currentColumns: ComputedRef<TableColumn[]>
  currentTableData: ComputedRef<any[]>
  refreshTime: Ref<string>
  refresh: () => void
}

const STOCK_COLUMNS: TableColumn[] = [
  { prop: 'code', label: '代码', minWidth: '80' },
  { prop: 'name', label: '名称', minWidth: '140' },
  { prop: 'marketValue', label: '总市值', minWidth: '120' },
  { prop: 'available', label: '可用', minWidth: '110' },
  { prop: 'hold', label: '持有', minWidth: '110' },
  { prop: 'cost', label: '成本', minWidth: '100', showTip: true, tipContent: '「成本价」仅供参考，成本价=（买入前的平均成本价x数量+此次买入的价格数x数量）/买入后持有数量。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「成本价」，请电邮至cs@goldhorse.com.hk。' },
  { prop: 'currentPrice', label: '现价', minWidth: '100' },
  { prop: 'profit', label: '盈亏', minWidth: '120', showTip: true, tipContent: '「盈亏」仅供参考，该数据或未能反映整体持仓的实时盈亏。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「盈亏」，请电邮至cs@goldhorse.com.hk。', type: 'twoLines', propList: [{ prop: 'profit' }, { prop: 'profitPercent' }] },
  { prop: '', label: '操作', type: 'action', fixed: 'right', align: 'center', minWidth: '152' }
]

const FUND_COLUMNS: TableColumn[] = [
  // { prop: 'name', label: '名称', minWidth: '220' },
  { prop: 'name', label: '名称/ISIN', minWidth: '220', type: 'tagWithText', separator: ' / ', propList: [{ prop: 'productName' }, { prop: 'productCode' }] },
  { prop: 'marketValue', label: '总市值', minWidth: '120' }
]

const BOND_COLUMNS: TableColumn[] = [
  { prop: 'underlying', label: '名称' },
  { prop: 'stockCode', label: 'ISIN', minWidth: '120' },
  { prop: 'marketValueDisplay', label: '市值' },
  { prop: 'currentAmount', label: '持有份数' },
  { prop: 'maturityDays', label: '剩余天数' },
  { prop: 'yieldToMaturity', label: '到期收益率' },
  { prop: 'maturityDate', label: '到期时间' }
]

const SPS_COLUMNS: TableColumn[] = [
  { prop: 'subType', label: '产品/ISIN', fixed: 'left', minWidth: '260', type: 'tagWithText', propList: [{ prop: 'subType', style: 'tag' }, { prop: 'stockCode' }] },
  { prop: 'investmentAmount', label: '投资金额', minWidth: '150' },
  { prop: 'issuer', label: '发行商', minWidth: '120' },
  { prop: 'underlying', label: '标的', minWidth: '220' },
  { prop: 'couponRatio', label: '年化票息', minWidth: '120' },
  { prop: 'maturityDate', label: '到期时间', minWidth: '124' }
]

const EXTRA_COLUMNS: TableColumn[] = [
  { prop: 'name', label: '名称/ISIN', minWidth: '220', type: 'tagWithText', separator: ' / ', propList: [{ prop: 'stockName' }, { prop: 'stockCode' }] },
  { prop: 'marketValueHkdDisplay', label: '总市值', minWidth: '120' }
]

const COLUMNS_MAP: Record<string, TableColumn[]> = {
  stock: STOCK_COLUMNS,
  fund: FUND_COLUMNS,
  bond: BOND_COLUMNS,
  sps: SPS_COLUMNS,
  extra: EXTRA_COLUMNS
}

const MARKET_LABEL_MAP: Record<string, string> = {
  HK: '港股总市值',
  US: '美股总市值',
  A: 'A股总市值'
}

const EXT_TYPE_MAP: Record<string, string> = {
  fund: 'fund',
  bond: 'bond',
  sps: 'sps',
  extra: 'extra'
}

function formatPercent(val: any): string {
  if (val == null || val === '') return '--'
  const num = Number(val)
  if (isNaN(num)) return '--'
  const percent = num * 100
  const truncated = Math.floor(percent * 100) / 100
  return truncated.toFixed(2) + '%'
}

export function useAccountDetail(options: UseAccountDetailOptions): UseAccountDetailReturn {
  const { clientId, activeTopTab, activeSubTab } = options

  const loading = ref(false)
  const refreshTime = ref(dayjs().format('YYYY-MM-DD HH:mm:ss'))

  const positionData = ref<any>({})
  const fundInfoData = ref<any>({})
  const lifecycleData = ref<any>({})

  const { refetch, onResult } = useQuery(gql.lpoaPositionFundLifecycle, { filter: {} })

  onResult((res) => {
    const { lpoaPositionList, lpoaFundInfoList, clientLifecycle } = res?.data || {}

    positionData.value =
      lpoaPositionList?.data?.find(
        (item: any) => item.clientid === clientId.value
      ) || {}

    fundInfoData.value =
      lpoaFundInfoList?.data?.find(
        (item: any) => item.clientid === clientId.value
      ) || {}

    lifecycleData.value =
      clientLifecycle?.data?.find(
        (item: any) => item.clientId === clientId.value
      ) || {}

    loading.value = false
    refreshTime.value = dayjs().format('YYYY-MM-DD HH:mm:ss')
  })

  // ---- cards 计算 ----

  const stockCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const fundInfo = fundInfoData.value?.info?.find(
      (item: any) => item.exchangeType.toLowerCase() === sub.toLowerCase()
    ) || {}

    return [
      {
        label: MARKET_LABEL_MAP[sub] || '总市值',
        value: numFormat(fundInfo.marketValue, 2) || '--'
      },
      {
        label: '现金可提',
        value: numFormat(fundInfo.fetchBalance, 2) || '--',
        showTip: true,
        tipContent: '「现金可提」仅供参考，该数据或未能反映账户的实时现金可提的金额，以及或未包含其他影响现金可提的收入和费用，如交易费用和处理费用等。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「现金可提」，请电邮至cs@goldhorse.com.hk。'
      },
      {
        label: '最大购买力',
        value: numFormat(fundInfo.enableBalance, 2) || '--',
        showTip: true,
        tipContent: '「最大购买力」仅供参考，该数据或未能反映帐户的实时最大购买力，以及或未包含其他影响最大购买力的收入和费用等。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「最大购买力」，请电邮至cs@goldhorse.com.hk。'
      },
      {
        label: '冻结资金',
        value: numFormat(fundInfo.frozenBalance, 2) || '--'
      }
    ]
  })

  const getExtItem = (type: string, moneyType?: string) => {
    const list: any[] = positionData.value?.extPosInfo || []
    return list.find((item: any) =>
      item.extType.toLowerCase() === type.toLowerCase() && (!moneyType || item.extMoneyType === moneyType)
    ) || {}
  }

  const fundCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const isHKD = sub === 'HKD'
    const extItem = getExtItem('fund', sub)

    return [
      {
        label: isHKD ? '资产净值(港元)' : '资产净值(美元)',
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const bondCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const isHKD = sub === 'bondHKD'
    const moneyType = isHKD ? 'HKD' : 'USD'
    const extItem = getExtItem('bond', moneyType)

    return [
      {
        label: isHKD ? '资产净值(港元)' : '资产净值(美元)',
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const spsCards = computed<CardItem[]>(() => {
    const extItem = getExtItem('sps', 'HKD')
    return [
      {
        label: '持仓净值(港元)',
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const extraCards = computed<CardItem[]>(() => {
    const extItem = getExtItem('extra', 'HKD')
    return [
      {
        label: '持仓净值(港元)',
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const currentCards = computed<CardItem[]>(() => {
    switch (activeTopTab.value) {
      case 'stock':
        return stockCards.value
      case 'fund':
        return fundCards.value
      case 'bond':
        return bondCards.value
      case 'sps':
        return spsCards.value
      case 'extra':
        return extraCards.value
      default:
        return []
    }
  })

  // ---- columns 计算 ----

  const currentColumns = computed<TableColumn[]>(() => {
    return COLUMNS_MAP[activeTopTab.value] || []
  })

  // ---- table 计算 ----

  const currentTableData = computed<any[]>(() => {
    const top = activeTopTab.value
    const sub = activeSubTab.value
    const pd = positionData.value
    let rows: any[] = []
    if (top === 'stock') {
      rows = pd?.info?.rows || []
    } else {
      const extType = EXT_TYPE_MAP[top]
      let moneyType = sub || 'HKD'
      if (top === 'bond') {
        moneyType = sub === 'bondHKD' ? 'HKD' : 'USD'
      } else if (!sub) {
        moneyType = 'HKD'
      }
      const extItem = pd?.extPosInfo?.find((item: any) =>
        item.extType?.toLowerCase() === extType?.toLowerCase() &&
        item.extMoneyType === moneyType
      )
      rows = extItem?.extPositionInfo?.rows || []
    }

    if (top === 'stock') {
      const filtered = rows.filter((item: any) => {
        if (sub === 'A') {
          return ['SH', 'SZ', 'A'].includes(item.exchangeType)
        }
        return item.exchangeType === sub
      })

      return filtered.map((item: any) => ({
        code: item.stockCode,
        name: item.stockNameGb,
        marketValue: numFormat(item.marketValue, 2),
        available: item.enableAmount?.toLocaleString() ?? '--',
        hold: item.currentAmount?.toLocaleString() ?? '--',
        cost: numFormat(item.costPrice, 3),
        currentPrice: item.currentPriceDisplay ?? '--',
        profit: item.incomeBalance != null ? numFormat(item.incomeBalance, 2) : '--',
        profitPercent: item.incomePercentDisplay ?? '--',
        exchangeType: item.exchangeType,
        fundAccount: item.fundAccount,
        stockCode: item.stockCode,
        stockNameGb: item.stockNameGb
      }))
    }

    if (top === 'fund') {
      const filtered = rows.filter((item: any) => item.moneyType === sub)
      return filtered.map((item: any) => ({
        name: item.stockCode,
        marketValue: item.marketValueDisplay || '--',
        productName: item.stockName || '--',
        productCode: item.stockCode || '--'
      }))
    }

    if (top === 'bond') {
      const bondMoneyType = sub === 'bondHKD' ? 'HKD' : 'USD'
      const filtered = rows.filter((item: any) => item.moneyType === bondMoneyType)
      return filtered.map((item: any) => ({
        stockCode: item.stockCode,
        underlying: item.underlying,
        marketValueDisplay: item.marketValueDisplay || '--',
        currentAmount: item.currentAmount || '--',
        maturityDays: item.maturityDays || '--',
        yieldToMaturity: item.yieldToMaturity || '--',
        maturityDate: item.maturityDate || '--'
      }))
    }

    if (top === 'sps') {
      const lifecycles: any[] = lifecycleData.value?.lifecycles || []
      const filtered = rows.filter((item: any) => {
        if (item.subType === 'FCN') {
          return lifecycles.some((lc: any) => lc.isin === item.stockCode)
        }
        return true
      })
      return filtered.map((item: any) => {
        let lifecycle: any = {}
        if (item.subType === 'FCN') {
          lifecycle = lifecycles.find((lc: any) => lc.isin === item.stockCode) || {}
        }
        return {
          ...item,
          subType: lifecycle?.productType || item.subType || '--',
          stockCode: lifecycle?.isin || item.stockCode || '--',
          investmentAmount: item.marketValueDisplay + item.moneyType || '--',
          couponRatio: lifecycle.couponRatio ? formatPercent(lifecycle.couponRatio) : item.yieldToMaturity || '--',
          underlying: lifecycle.underlyingPerformance?.map((item: { code: string }) => item.code).join('+') || item.underlying || '--'
        }
      })
    }

    if (top === 'extra') {
      return rows.map((item: any) => ({
        stockName: item.stockName,
        stockCode: item.stockCode,
        marketValueHkdDisplay: item.marketValueHkdDisplay || '--'
      }))
    }

    return []
  })

  // ---- 操作方法 ----

  const refresh = () => {
    loading.value = true
    refetch({ filter: {} })
  }

  return {
    loading,
    currentCards,
    currentColumns,
    currentTableData,
    refreshTime,
    refresh
  }
}
